import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Save, Loader2, Eye, Palette } from "lucide-react";
import { toast } from "sonner";
import { adminGetInvoiceSettings, adminPatchInvoiceSettings, API } from "../../lib/api";

/**
 * Admin → Invoice Settings.
 * Single global doc keyed _id='global'. Edits propagate immediately to all
 * customer-facing invoice emails + PDFs (Iter 37 template editor).
 */
export default function InvoiceSettingsTab({ pw }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    adminGetInvoiceSettings(pw).then(setForm).catch(() => toast.error("Could not load settings"));
  }, [pw]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const next = await adminPatchInvoiceSettings(form, pw);
      setForm(next);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API}/admin/invoice-settings/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setPreviewHtml(data.html || "");
    } catch {
      toast.error("Preview failed");
    } finally { setPreviewLoading(false); }
  };

  if (!form) return <div className="font-mono text-sm text-zinc-500">Loading settings…</div>;

  return (
    <div data-testid="invoice-settings-tab" className="grid lg:grid-cols-2 gap-6 max-w-[1400px]">
      <div>
        <h2 className="font-display text-3xl uppercase flex items-center gap-3 mb-6">
          <SettingsIcon size={22} className="text-[var(--tc-red)]" /> Invoice Settings
        </h2>

        <fieldset className="border border-[#222] p-4 mb-4">
          <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">BUSINESS DETAILS</legend>
          <div className="space-y-3">
            <Row label="Business name" value={form.business_name} onChange={(v) => set({ business_name: v })} />
            <Row label="Business email" value={form.business_email} onChange={(v) => set({ business_email: v })} />
            <Row label="Business phone" value={form.business_phone} onChange={(v) => set({ business_phone: v })} />
            <Row label="Business address" value={form.business_address || ""} onChange={(v) => set({ business_address: v })} />
            <Row label="Logo URL (optional)" value={form.business_logo_url || ""} onChange={(v) => set({ business_logo_url: v })} hint="Shown at the top of every invoice email" testId="settings-logo-url" />
          </div>
        </fieldset>

        <fieldset className="border border-[#222] p-4 mb-4">
          <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2 flex items-center gap-2">
            <Palette size={11} /> BRANDING (EMAIL + PDF)
          </legend>
          <div className="space-y-3">
            <label className="block" data-testid="settings-accent-row">
              <span className="font-mono text-[10px] tracking-widest text-zinc-500">ACCENT COLOR</span>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={form.accent_color || "#E41E2B"}
                  onChange={(e) => set({ accent_color: e.target.value })}
                  data-testid="settings-accent-color"
                  className="w-12 h-10 bg-black border border-[#222] cursor-pointer"
                />
                <input
                  type="text"
                  value={form.accent_color || "#E41E2B"}
                  onChange={(e) => set({ accent_color: e.target.value })}
                  className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm font-mono outline-none"
                />
              </div>
              <span className="font-mono text-[10px] text-zinc-500 mt-1 block">Used on buttons, headers and totals across emails &amp; PDF</span>
            </label>
            <Row label="Email header text" value={form.email_header_text || ""} onChange={(v) => set({ email_header_text: v })}
                 hint="Optional alternative to business name in the email header" testId="settings-header-text" />
            <Row label="Email footer signature" textarea value={form.email_footer_signature || ""} onChange={(v) => set({ email_footer_signature: v })}
                 hint="Plain text or HTML — appears at the bottom of every invoice email" testId="settings-footer-signature" />
            <div className="grid grid-cols-1 gap-2 pt-2 border-t border-[#222]">
              <Toggle label="Show e-transfer block in emails &amp; PDF" value={form.show_etransfer_block !== false} onChange={(v) => set({ show_etransfer_block: v })} testId="settings-toggle-etransfer" />
              <Toggle label="Show deposit option in emails &amp; PDF" value={form.show_deposit_block !== false} onChange={(v) => set({ show_deposit_block: v })} testId="settings-toggle-deposit" />
              <Toggle label="Show terms block on PDF" value={form.show_terms_block !== false} onChange={(v) => set({ show_terms_block: v })} testId="settings-toggle-terms" />
            </div>
          </div>
        </fieldset>

        <fieldset className="border border-[#222] p-4 mb-4">
          <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">DEFAULTS</legend>
          <div className="space-y-3">
            <Row label="Starting invoice number" type="number" value={form.starting_number} onChange={(v) => set({ starting_number: Number(v) || 1001 })}
                 hint="The next invoice created will use max(this, last+1)" testId="settings-starting-number" />
            <Row label="Default tax rate (decimal, e.g. 0.13)" type="number" step="0.01" value={form.default_tax_rate} onChange={(v) => set({ default_tax_rate: Number(v) || 0.13 })} />
            <Row label="Default deposit %" type="number" value={form.default_deposit_pct} onChange={(v) => set({ default_deposit_pct: Math.max(0, Math.min(99, Number(v) || 50)) })} />
            <Row label="Default e-transfer instructions" textarea value={form.default_etransfer_instructions || ""} onChange={(v) => set({ default_etransfer_instructions: v })} testId="settings-etransfer" />
            <Row label="Default customer notes" textarea value={form.default_customer_notes || ""} onChange={(v) => set({ default_customer_notes: v })} />
            <Row label="Default invoice terms" textarea value={form.default_terms || ""} onChange={(v) => set({ default_terms: v })}
                 hint="Shown on the PDF if 'Show terms block' is enabled" testId="settings-terms" />
            <Row label="Email subject prefix" value={form.email_subject_prefix || ""} onChange={(v) => set({ email_subject_prefix: v })}
                 hint="E.g. '[Toronto Custom]' — prefixed to every invoice email subject" />
          </div>
        </fieldset>

        <div className="flex gap-3">
          <button data-testid="settings-save" onClick={save} disabled={saving} className="btn-tc-primary inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save Settings</>}
          </button>
          <button data-testid="settings-preview" onClick={loadPreview} disabled={previewLoading} className="font-mono text-xs tracking-widest border border-[var(--tc-blue)] text-[var(--tc-blue)] hover:bg-[var(--tc-blue)] hover:text-white px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50">
            {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} PREVIEW EMAIL
          </button>
        </div>
      </div>

      {/* Live preview pane */}
      <div className="border border-[#222] bg-zinc-950" data-testid="settings-preview-pane">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 p-3 border-b border-[#222]">
          EMAIL PREVIEW — &ldquo;INVOICE SENT&rdquo;
        </div>
        {previewHtml ? (
          <iframe
            title="email-preview"
            srcDoc={previewHtml}
            sandbox=""
            data-testid="settings-preview-iframe"
            className="w-full min-h-[640px] bg-black"
          />
        ) : (
          <div className="p-12 text-center text-zinc-600 font-mono text-xs">
            Click &ldquo;Preview Email&rdquo; to render the customer email with your current settings.
            <br /><br />Changes you make here apply to <strong>every</strong> invoice email + PDF as soon as you save.
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, onChange, type = "text", step, textarea, hint, testId }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-widest text-zinc-500">{label.toUpperCase()}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
                  data-testid={testId}
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 text-sm outline-none resize-y" />
      ) : (
        <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
               data-testid={testId}
               className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 text-sm outline-none" />
      )}
      {hint && <span className="font-mono text-[10px] text-zinc-500 mt-1 block">{hint}</span>}
    </label>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer py-1">
      <span className="font-mono text-[11px] text-zinc-300" dangerouslySetInnerHTML={{ __html: label }} />
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testId}
        aria-pressed={value}
        className={`relative w-10 h-5 rounded-full transition-colors ${value ? "bg-[var(--tc-red)]" : "bg-zinc-700"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}
